import {
  type AuthStrategyKind,
  USER_CREATABLE_AUTH_KINDS,
} from "@memkernel/integration-kit";
import { api } from "./api";

/**
 * Credential kind stored on the server. Adds the legacy `"oauth"`
 * marker (kept for backward-compat with existing DB rows that
 * pre-date the renaming) plus a `"username_password"` alias of
 * `"basic"` used by some integrations.
 */
export type CredentialKind = AuthStrategyKind | "oauth" | "username_password";

export interface Credential {
  id: string;
  integration: string;
  kind: CredentialKind;
  label: string;
  providerAccountId: string;
  scopes: string[];
  accessTokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `authKind` is required when an integration offers more than one
 * user-creatable strategy. Derived from the kit's registry, so adding
 * a new kind to the kit propagates here automatically.
 */
export type UserCreatableAuthKind = (typeof USER_CREATABLE_AUTH_KINDS)[number];

export interface CreateCredentialInput {
  integration: string;
  authKind?: UserCreatableAuthKind;
  label?: string;
  fields?: Record<string, string>;
}

export const credentialsApi = {
  list: (integration?: string) =>
    api<Credential[]>(
      integration
        ? `/v1/credentials?integration=${encodeURIComponent(integration)}`
        : `/v1/credentials`,
    ),
  create: (input: CreateCredentialInput) =>
    api<Credential>("/v1/credentials", { method: "POST", json: input }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/v1/credentials/${id}`, {
      method: "DELETE",
    }),
};

/**
 * Begin the OAuth dance. Authenticated POST that returns the
 * upstream authorize URL — the caller then navigates to it with
 * `window.location.href = url`. Bearer + scope headers go through
 * the api() helper as usual; no token or scope is leaked into the
 * URL or relied on via cookies.
 *
 *   mode = "built_in" (default) — uses MemKernel's bundled OAuth app.
 *   mode = "byo"                — uses the OAuth app the user saved
 *                                 for this project + integration via
 *                                 POST /v1/oauth-apps.
 */
export async function startOAuth(
  slug: string,
  mode: "built_in" | "byo" = "built_in",
): Promise<{ authorizeUrl: string }> {
  return api<{ authorizeUrl: string }>(
    `/api/oauth/start/${encodeURIComponent(slug)}`,
    { method: "POST", json: { mode } },
  );
}
